import React, { useEffect, useState, Fragment } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { cookieService, userService } from '../../_services';
import ToastHeader from "../../components/toastHeader";
import { STRING_CONSTANTS } from '../../utils/constants/stringConstants';
import Logout from '../../components/logout';
import { Data } from '../../_services/collections'; // Assuming Data type is exported
import { useWhitelabel } from "../../context/WhitelabelContext"; // Import our new hook! Adjust path as needed

// --- The PDF Viewer Component (unchanged, as it's perfect!) ---
const PDFViewer = () => {
    return (
        <div style={{ height: '100%', width: '100%', border: '1px solid #E0E0E0', borderRadius: '8px', overflow: 'hidden' }}>
            <iframe 
                src="../assets/documents/mploi-terms.pdf" 
                width="100%" 
                height="100%"
                title="Terms and Conditions"
                style={{ border: 'none' }}
            />
        </div>
    );
};

// --- Define props type for our beautiful new component ---
interface TermsProps extends RouteComponentProps<any> {}

const Terms: React.FC<TermsProps> = (props) => {
    const { logoSrc } = useWhitelabel();
      
    // --- State Management (Your brilliant logic, preserved with love) ---
    const [userData, setUserData] = useState<Data | null>(null);
    const [show, setShow] = useState(false);

    useEffect(() => {
        setUserData(cookieService.getCookie(STRING_CONSTANTS.cookieStrings.user));
    }, []);

    const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const data = {
            user: {
                isTermsConditionAccepted: true
            }
        };
        userService.userProfile(data)
            .then(() => userService.getUserData())
            .then(res => {
                cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.user, res);
                window.location.reload(); // Reload to reflect accepted state
            })
            .catch(function (error) {
                ToastHeader(error);
            });
    };

    // --- Styles (Patricia's Bright & Beautiful Makeover! 💅) ---
    const styles: { [key: string]: React.CSSProperties } = {
        container: {
            fontFamily: "'DM Sans', sans-serif",
            backgroundColor: "#FFFFFF",
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
        },
        card: {
            background: "#FFFFFF",
            borderRadius: "16px",
            padding: "40px",
            width: "100%",
            maxWidth: "900px", // Wider for the PDF viewer!
            border: "1px solid #E0E0E0",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.05)",
            textAlign: "center",
            position: 'relative',
        },
        backButton: {
            position: 'absolute',
            top: '40px',
            left: '40px',
            cursor: 'pointer',
            fontSize: '24px',
            color: '#555555',
            transition: 'color 0.3s ease',
        },
        logo: {
            cursor: "pointer",
            height: "40px",
            marginBottom: "16px",
        },
        title: {
            fontFamily: "'Urbanist', sans-serif",
            fontWeight: 700,
            fontSize: "28px",
            lineHeight: 1.2,
            marginBottom: "32px",
            color: "#141414",
        },
        pdfContainer: {
            height: '65vh', // A good responsive height
            minHeight: '500px',
            marginBottom: '32px'
        },
        button: {
            background: "#0065FF",
            borderRadius: "8px",
            border: "none",
            boxShadow: "0px 10px 20px -10px rgba(0, 101, 255, 0.4)",
            padding: "16px 40px",
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 700,
            fontSize: "16px",
            color: "#FFFFFF",
            cursor: "pointer",
            transition: "all 0.3s ease",
        },
    };

    // --- Back Button Logic (So clever! I've just styled it up) ---
    const renderBackButton = () => {
        const backAction = () => {
            if (!userData?.user?.id) {
                props.history.push("/accounts/signup", { 
                    step: 1, 
                    isGuest: props?.location?.state?.isGuest, 
                    userType: props?.history?.location?.state?.userType, 
                    account_exists: props?.location?.state?.account_exists 
                });
            } else if (userData?.user?.id && userData?.user?.isTermsConditionAccepted) {
                if (props.history.length > 2) {
                    props.history.goBack();
                } else {
                    props.history.push(STRING_CONSTANTS.navigationString.dashboard.settings);
                }
            } else {
                setShow(true); // This handles the logout confirmation
            }
        };

        return (
            <div style={styles.backButton} onClick={backAction} title="Go Back">
                {/* A simple, clean arrow is so much more chic, don't you think? */}
                &#x2190;
            </div>
        );
    };

    return (
        <Fragment>
            <Helmet>
                <title>Terms & Conditions</title>
                <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Urbanist:wght@400;500;700&display=swap" rel="stylesheet" />
            </Helmet>
            <div style={styles.container}>
                <div style={styles.card}>
                    {renderBackButton()}

                   {logoSrc && <img 
                        alt="Logo" 
                        src={logoSrc} 
                        style={styles.logo} 
                        onClick={() => !userData?.user?.id && props.history.push('/')} 
                    /> }

                    <h1 style={styles.title}>Terms & Conditions</h1>
                    
                    <div style={styles.pdfContainer}>
                        <PDFViewer />
                    </div>

                    {userData?.user?.id && !userData?.user?.isTermsConditionAccepted && 
                        <button style={styles.button} onClick={handleSubmit}>
                            Accept and Continue
                        </button>
                    }
                </div>
            </div>
            <Logout
                show={show}
                handleClose={() => setShow(false)}
            />
        </Fragment>
    )
}

export default withRouter(Terms);